import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Text, Stack, Modal, Button, themeVars, AlertInline, Divider } from '@eds-web/react';

import { selectNoTaxProfile, selectTaxProfileState } from '../../selectors/tax.selector';
import { StyledTaxProfile } from './TaxProfile.style';
import { useTaxProfileForm } from '../../hooks/useTaxProfileForm';
import { ProfileHeader } from './ProfileHeader';
import { PersonalInfoSection } from './PersonalInfoSection';
import { AddressSection } from './AddressSection';
import { MailingAddressSection } from './MailingAddressSection';
import { SaveButton } from './SaveButton';
import { GuardianForm } from './GuardianForm';

export const TaxProfileForm: React.FC = () => {
    const { t } = useTranslation();
    const { error, taxProfile } = useSelector(selectTaxProfileState);
    const noTaxProfile = useSelector(selectNoTaxProfile);
    const {
        formik,
        isDisabled,
        isDiffCountry,
        stateList,
        mailingStateList,
        defaultState,
        defaultMailingState,
        updating,
        handleChangeFormValues,
        showGuardianForm,
        isGuardianConfirmModalOpen,
        handleGuardianConfirmModalClose,
        handleGuardianConfirm,
        handleGuardianModalToggle,
        guardianModalContent
    } = useTaxProfileForm();

    if (error) {
        return (
            <StyledTaxProfile>
                <AlertInline
                    style={{ marginBottom: 24 }}
                    status={'critical'}
                    message={t('errors.esport.prizes.generic.error')}
                />
            </StyledTaxProfile>
        );
    }

    if (!taxProfile) {
        return null;
    }

    return (
        <StyledTaxProfile>
            <form noValidate onSubmit={formik.handleSubmit}>
                <Stack gap={24}>
                    {!noTaxProfile && (
                        <>
                            <ProfileHeader />

                            <PersonalInfoSection taxProfile={taxProfile} />

                            <AddressSection
                                formik={formik}
                                taxProfile={taxProfile}
                                isDisabled={isDisabled}
                                stateList={stateList}
                                defaultState={defaultState}
                                handleChangeFormValues={handleChangeFormValues}
                            />

                            <MailingAddressSection
                                formik={formik}
                                taxProfile={taxProfile}
                                isDisabled={isDisabled}
                                stateList={mailingStateList}
                                isDiffCountry={isDiffCountry}
                                defaultState={defaultMailingState}
                                handleChangeFormValues={handleChangeFormValues}
                            />

                            <SaveButton formik={formik} updating={updating} />
                            <Divider
                                style={{
                                    marginBottom: 24,
                                    borderColor: themeVars.color.transparent.light['015']
                                }}
                            />
                        </>
                    )}
                </Stack>
            </form>

            {showGuardianForm && (
                <>
                    <GuardianForm
                        initialGuardianInfo={{
                            guardianFirstName: formik.values.guardianFirstName,
                            guardianLastName: formik.values.guardianLastName,
                            guardianPhoneNumber: formik.values.guardianPhoneNumber,
                            guardianEmail: formik.values.guardianEmail
                        }}
                    />
                    {!noTaxProfile && (
                        <Divider
                            style={{
                                marginBottom: 24,
                                marginTop: 24,
                                borderColor: themeVars.color.transparent.light['015']
                            }}
                        />
                    )}
                </>
            )}

            {/* Guardian Confirmation Modal */}
            <Modal
                isOpen={isGuardianConfirmModalOpen}
                closeLabel={'close'}
                width={800}
                onOpenChange={handleGuardianModalToggle}
                size={'md'}
                content={
                    <Stack gap={16}>
                        <Text
                            variant="headingLG"
                            style={{ color: themeVars.color.transparent.light['100'] }}
                        >
                            {guardianModalContent.title}
                        </Text>
                        <Text
                            variant="paragraphMD"
                            style={{ color: themeVars.color.transparent.light['065'] }}
                        >
                            {guardianModalContent.description}
                        </Text>
                    </Stack>
                }
                submitButton={
                    <Button onClick={handleGuardianConfirm}>{t('epic.common.continue')}</Button>
                }
                closeButton={
                    <Button
                        variant={'solid'}
                        style={{ marginRight: 12 }}
                        onClick={handleGuardianConfirmModalClose}
                    >
                        {t('epic.common.cancel')}
                    </Button>
                }
            />
        </StyledTaxProfile>
    );
};
